import { Button, LabelledField, LoadingModal } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import Form from "components/Forms/Form";
import FormButtons from "components/Forms/FormButtons";
import { ModalFormField } from "components/Forms/FormRenderers/ModalFormRenderer";
import { PdfUpload } from "components/Forms/PdfUpload";
import AppModal from "components/common/AppModal";
import { injectModal } from "lib/injectParams";
import { useMemo, useState } from "react";
import { DocumentNewModalController } from "./DocumentNewModalController";
import { AppController } from "controllers/AppController";

interface Props {
  clubId: string;
}

const DocumentNewModal: React.FC<Props> = ({ clubId }) => {
  const appController = AppController.use();
  const controller = DocumentNewModalController.use();
  const { saving, record, loading } = controller.state;
  const [uploading, setUploading] = useState(false);

  const handleUpload = (documentId: string) => {
    controller.actionSetDocumentId(documentId);
  };

  const fields = useMemo(() => controller.fields(), []);

  if (loading) return <LoadingModal title="Upload new document" />;

  if (appController.state.online === false) {
    return (
      <AppModal title="Upload new document" className="max-h-[80vh]">
        <div className="p-4">You are offline. Connect to the Internet to upload a document.</div>
        <div className="flex justify-center">
          <Button onClick={() => appController.actionCloseModal()}>Close</Button>
        </div>
      </AppModal>
    );
  }

  return (
    <AppModal title="Upload new document" className="max-h-[80vh]">
      <Form name="new_document">
        <fieldset disabled={saving}>
          <ModalFormField field={fields.name} record={record} autoFocus />

          <LabelledField
            label="Document file"
            helpText="The file is private. Only people who can see this document can open it."
          >
            <PdfUpload
              clubId={clubId}
              fileStorage="private_documents"
              onChange={handleUpload}
              onUploadStart={() => setUploading(true)}
              onUploadEnd={() => setUploading(false)}
              value={record?.document_id || ""}
              className="overflow-y-auto max-h-[30vh]"
            />
          </LabelledField>

          <ModalFormField field={fields.audience} record={record} />
          <ModalFormField field={fields.acceptance} record={record} />
        </fieldset>
        {controller.state.errors.document_id && (
          <p className="mb-2 text-sm text-red-600">{controller.state.errors.document_id}</p>
        )}
        <FormButtons saveTitle="Upload" disabled={uploading || !record?.document_id} />
      </Form>
    </AppModal>
  );
};

export default injectModal(DocumentNewModalController.scope(ApplicationView(DocumentNewModal)));
